<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'

import 'mapbox-gl/dist/mapbox-gl.css'
import mapboxgl from 'mapbox-gl'
import MapboxCompare from '@/utils/mapbox/compare'
import { MAPBOX_ACCESS_TOKEN } from '@/config/mapbox'

import { MapSource, MapLayer } from '@/components/map'
import {
  DialogCreate,
  MapProjectLayer,
  MapProjectBoundaries,
  MapDraw,
  MapRasterLayers,
  MapSettings
} from '@/components/map'
import { useZLayers } from '@/composables/useZLayers'

mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN

const props = defineProps({
  activeTab: {
    type: String,
    default: 'projects'
  }
})

const renderIncrement = ref(0)
const rennderIncrement2 = ref(0)

const route = useRoute()
const projectId = computed(() => parseInt(route.params.project_id))

const store = useStore()
const map = computed(() => store.getters['geo/getMap'])
const map2 = computed(() => store.getters['geo/getMap2'])
const zLayers = useZLayers(map)
const isCompareMode = computed(() => store.getters['geo/getIsCompareMode'])
const mapReady = computed(() => store.getters['geo/getMapReady'])
const projects = computed(() => store.getters['project/getProjects'])
const flatedProjects = computed(() => store.getters['project/getFlatedProjects'])

const currentMapZoomLevel = ref(null)
const projectsToRender = computed(() => {
  if (!projectId.value) {
    return projects.value
  } else {
    const currentProject = flatedProjects.value.find((project) => project.project_id === projectId.value)

    if (!currentProject) return null

    if (currentProject?.children?.length > 0) {
      return [...currentProject.children, currentProject]
    } else {
      return [currentProject]
    }
  }
})

const params = computed(() => store.getters['search/getParams'])

const mapParams = computed(() => {
  const zoom = params.value.find((param) => param.key === 'zoom')
  const centerLat = params.value.find((param) => param.key === 'center_lat')
  const centerLng = params.value.find((param) => param.key === 'center_lng')

  return zoom && centerLat && centerLng ? {
    center: [centerLng.value, centerLat.value],
    zoom: zoom.value
  } : null
})

const getLocalMapOptions = () => {
  try {
    const stored = localStorage.getItem('mapOptions')
    return stored ? JSON.parse(stored) : null
  } catch (e) {
    console.warn('Unable to parse mapOptions from localStorage:', e)
    return null
  }
}

const defaultOptions = {
  center: [-91.874, 42.76],
  zoom: 4
}

class CompareControl {
  onAdd(map) {
    const div = document.createElement('div');
    div.className = 'mapboxgl-ctrl mapboxgl-ctrl-group'
    div.innerHTML = `<button class="compare-swiper-horizontal" title="Compare mode" area-label="Compare mode" style="display: flex; align-items: center; justify-content: center;">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-separator-horizontal"><line x1="3" x2="21" y1="12" y2="12"/><polyline points="8 8 12 4 16 8"/><polyline points="16 16 12 20 8 16"/></svg>
    </button>`
    div.addEventListener('contextmenu', (e) => e.preventDefault())
    div.addEventListener('click', () => store.commit('geo/SET_COMPARE_MODE', !isCompareMode.value))
    return div
  }
}

const transformRequest = (url, resourceType) => {
  if (url.includes('/tulum/tiles/')) {
    const accessToken = localStorage.getItem('accessToken')
    if (accessToken) {
      return {
        url: url,
        headers: { 'Authorization': 'Bearer ' + accessToken }
      }
    }
  }
}

const createMap = async () => {
  const mapInstance = new mapboxgl.Map({
    container: 'mapDiv',
    minZoom: 0,
    maxZoom: 22,
    logoPosition: 'bottom-right',
    style: 'mapbox://styles/mapbox/outdoors-v12?optimize=true',
    ...mapParams.value ? mapParams.value : getLocalMapOptions() ?? defaultOptions,
    transformRequest: transformRequest
  })


  await store.commit('geo/SET_MAP', mapInstance)

  map.value.addControl(new mapboxgl.NavigationControl(), 'bottom-right')
  map.value.addControl(
    new mapboxgl.GeolocateControl({
      positionOptions: {
        enableHighAccuracy: true
      },
      trackUserLocation: true,
      showUserHeading: true
    }), 'bottom-right'
  )
  map.value.addControl(new CompareControl(), 'bottom-right')
}

const MAX_IMAGE_CACHE_SIZE = 100
const imageCache = new Map()

// Image processing constants - rounded square style
// We use a larger canvas size and pixelRatio to get crisp images at the desired display size
const IMAGE_SIZE = 100        // Canvas size (larger for crisp rendering)
const DISPLAY_SIZE = 50       // Desired display size on map
const PIXEL_RATIO = IMAGE_SIZE / DISPLAY_SIZE  // = 2 for retina-like quality
const BORDER_WIDTH = 4        // White border width (scaled with image size, displays as 2px)
const CORNER_RADIUS = 8       // ~8% corner radius for rounded squares (scaled)

function loadImageAndCache(code, url) {
  if (imageCache.has(code)) {
    // LRU: move accessed item to end (most recently used)
    const value = imageCache.get(code)
    imageCache.delete(code)
    imageCache.set(code, value)
    return value
  }

  // LRU eviction: remove oldest (first) entry if at capacity
  if (imageCache.size >= MAX_IMAGE_CACHE_SIZE) {
    const oldestKey = imageCache.keys().next().value
    imageCache.delete(oldestKey)
  }

  const loadingPromise = new Promise((resolve, reject) => {
    map.value.loadImage(url, (error, image) => {
      if (error) {
        return
      }

      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')

      canvas.width = IMAGE_SIZE
      canvas.height = IMAGE_SIZE

      // Clear canvas
      ctx.clearRect(0, 0, IMAGE_SIZE, IMAGE_SIZE)

      // Draw white background with rounded corners (for border effect)
      ctx.fillStyle = 'white'
      ctx.beginPath()
      ctx.roundRect(0, 0, IMAGE_SIZE, IMAGE_SIZE, CORNER_RADIUS)
      ctx.fill()

      // Create clipping path for image (inset by border width)
      const innerSize = IMAGE_SIZE - BORDER_WIDTH * 2
      const innerRadius = Math.max(0, CORNER_RADIUS - BORDER_WIDTH / 2)

      ctx.save()
      ctx.beginPath()
      ctx.roundRect(BORDER_WIDTH, BORDER_WIDTH, innerSize, innerSize, innerRadius)
      ctx.clip()

      // Draw image inside clipped area
      ctx.drawImage(image, BORDER_WIDTH, BORDER_WIDTH, innerSize, innerSize)
      ctx.restore()

      const resizedImage = ctx.getImageData(0, 0, IMAGE_SIZE, IMAGE_SIZE)

      // Check again before adding (race condition protection)
      if (!map.value.hasImage(code)) {
        map.value.addImage(code, resizedImage, { pixelRatio: PIXEL_RATIO })
      }

      resolve()
    })
  })

  imageCache.set(code, loadingPromise)
  return loadingPromise
}

onMounted(async () => {
  await createMap()

  map.value.on('load', async () => {
    // Initialize z-layers for stable layer ordering
    zLayers.init()

    await store.dispatch('geo/mapReady', true)

    // Always save camera position to localStorage on any map movement
    // This ensures position is persisted regardless of active tab or project context
    map.value.on('moveend', () => {
      localStorage.setItem('mapOptions', JSON.stringify({
        center: map.value.getCenter(),
        zoom: map.value.getZoom(),
        bearing: map.value.getBearing(),
        pitch: map.value.getPitch()
      }))
    })

    map.value.on('styleimagemissing', async (e) => {
      // e.id is url of the missing image
      await loadImageAndCache(e.id, e.id)
    })

    map.value.on('style.load', async () => {
      imageCache.clear()
    })
  })
})

const compare = ref(null)

// Reposition map when URL params change (e.g., navigating with different coords)
watch(
  () => mapParams.value,
  (newParams) => {
    if (newParams && map.value && mapReady.value) {
      map.value.jumpTo({
        center: newParams.center,
        zoom: newParams.zoom
      })
    }
  }
)

watch(() => isCompareMode.value, async (value) => {
  if (value) {
    const map2Instance = new mapboxgl.Map({
      container: 'mapDiv2',
      minZoom: 0,
      maxZoom: 22,
      style: 'mapbox://styles/mapbox/standard',
      transformRequest: transformRequest
    })
    store.commit('geo/SET_MAP2', map2Instance)

    map2.value.on('load', async () => {
      store.commit('geo/SET_MAP2_READY', true)

      map2.value.on('styleimagemissing', async (e) => {
        // e.id is url of the missing image
        await loadImageAndCache(e.id, e.id)
      })
    })

    compare.value = new MapboxCompare(map2.value, map.value, '#comparison', {
      orientation: 'horizontal'
    })
  } else {
    compare.value.remove()
    compare.value = null
    map2.value.remove()
    store.commit('geo/SET_MAP2', null)
    store.commit('geo/SET_MAP2_READY', false)
  }
})

</script>

<template>
  <div class="relative min-h-full w-full h-full bg-slate-100 overflow-hidden">
    <div id='comparison'>
      <div id='mapDiv2' class='map'></div>
      <div id='mapDiv' class='map'></div>
    </div>

    <div v-if="mapReady && map">
      <MapRasterLayers :map="map" />
      <MapProjectBoundaries :map="map" :active-tab="activeTab" />

      <MapSource
        :map="map"
        type="geojson"
        :id="'empty-source'"
        :data="{
          type: 'FeatureCollection',
          features: []
        }"
      >
        <MapLayer
          :map="map"
          :id="'default-layer'"
          :source="'empty-source'"
          type="fill"
        >
          <MapLayer
            :map="map"
            :id="'project-layer'"
            :source="'empty-source'"
            type="fill"
            :before-id="'default-layer'"
          >
            <div v-if="projectsToRender && projectsToRender.length && activeTab === 'projects'" :key="projectsToRender.length">
              <MapProjectLayer
                v-for="project in projectsToRender"
                :map="map"
                :key="project"
                :project="project"
                :active-tab="activeTab">
              </MapProjectLayer>
            </div>
          </MapLayer>
        </MapLayer>
      </MapSource>

      <MapDraw :map="map">
        <div v-if="projectId || activeTab === 'projects'" class="absolute z-50 bottom-16 md:bottom-12 mb-3 md:mb-1 right-10 py-2 px-3" :class="{'mt-9': !!projectId}">
          <div class="flex items-end justify-end">
            <DialogCreate />
          </div>
        </div>
      </MapDraw>
    </div>
  </div>
</template>

<style>
.slideRight-enter-active, .slideRight-leave-active {
  transition: transform 0.1s ease-in-out, opacity 0.1s ease-in-out;
}
.slideRight-enter-from, .slideRight-leave-to {
  transform: translateX(-3%);
  opacity: 0;
}

.map {
  position:absolute;
  top:0;
  bottom:0;
  width:100%;
}

.mapboxgl-compare {
  background-color: #fff;
  position: absolute;
  width: 2px;
  height: 100%;
  z-index: 1;
}

.mapboxgl-compare .compare-swiper-vertical {
  background-color: #0059FF;
  box-shadow:inset 0 0 0 2px #ffffff;
  display: inline-block;
  border-radius: 50%;
  position: absolute;
  width: 30px;
  height: 30px;
  top: 50%;
  left: -15px;
  margin: -15px 1px 0;
  color: #ffffff;
  cursor: ew-resize;
}

.mapboxgl-compare-horizontal {
  position: relative;
  width: 100%;
  height: 2px;
}
.mapboxgl-compare .compare-swiper-horizontal {
  background-color: #0059FF;
  box-shadow:inset 0 0 0 2px #fff;
  display: inline-block;
  border-radius: 50%;
  position: absolute;
  width: 30px;
  height: 30px;
  top: 50%;
  left: 50%;
  margin: -15px 1px 0;
  color: #ffffff;
  cursor: ns-resize;
  transform: rotate(90deg);
}
</style>
